<template>
  <header v-if="!fromDetailTemplate">
    <h3>Horario de ejecución</h3>
    <p>Elige los días y rango de horas en los que deseas que tu secuencia se lleve a cabo.</p>
  </header>

  <a-form ref="formRef" class="form" layout="vertical" :model="formState">
    <a-form-item label="Seleccione los días de ejecución" name="daysSelecteds" class="day-picker">
      <a-tooltip placement="right">
        <template #title v-if="disabled">
          <div>Es necesario inactivar la secuencia para poder modificar.</div>
        </template>
        <GroupSelectedButtons
          ref="groupSelectedButtons"
          :dataButtons="WeekDays"
          :allSelecteds="false"
          :disabled="disabled"
          @buttonSelecteds="setDaysSelected"
          :selecteds="formState.daysSelected"
        />
      </a-tooltip>
    </a-form-item>

    <p class="error" v-if="errorMessage">{{ errorMessage }}</p>

    <!-- <CustomScrollbar :height="fromDetailTemplate ? 'calc(100% - 170px)' : '250px'"> -->
    <CustomScrollbar :height="fromDetailTemplate ? 'calc(100% - 200px)' : 'calc(100% - 250px)'">
      <a-form-item
        v-if="formState.arrayRangePickers?.length || 0 > 0"
        label="Seleccione los rangos horarios por día"
        name="arrayRangePickers"
        style="width: 100%"
      >
        <list-range-pickers
          ref="arrayRangePickers"
          :fromTreeSection="fromDetailTemplate"
          :data-source="sortDataSource(formState.arrayRangePickers)?.slice()"
          :disabled="disabled"
          isSequence
          @onChangeRange="handleChangeRange"
          @onAddRange="handleAddRange"
          @onRemoveRange="handleRemoveRange"
        />
      </a-form-item>
    </CustomScrollbar>
  </a-form>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from "vue";
import { cloneDeep, isEqual } from "lodash";
import { FormInstance } from "ant-design-vue";

import { RawSchedule } from "@/@types/ivr/forms";
import { Schedule, Sequence } from "@/@types/flows/sequences";
import { groupScheduleConfig } from "@/app/chatbots/utils/groupScheduleConfig";
import { transformIndexDays } from "@/app/shared/utils/transform";
import { formatSchedulePicker } from "@/app/chatbots/utils/formatSchedulePicker";
import { ScheduleStepDto } from "@/@types/flows/sequences/dto";

import GroupSelectedButtons from "@/app/chatbots/components/buttons/GroupSelectedButtons.vue";
import ListRangePickers from "@/app/chatbots/components/organisms/ListRangePickers.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

const WeekDays = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];

const props = defineProps<{
  fromDetailTemplate?: boolean;
  disabled?: boolean;
  sequence?: Sequence;
}>();

const emit = defineEmits<{
  toggleButton: [disabled: boolean];
}>();

const formRef = ref<FormInstance>();
const formState = reactive<{ daysSelected?: number[]; arrayRangePickers?: RawSchedule[] }>({
  daysSelected: props.sequence?.schedules
    ? groupScheduleConfig(props.sequence?.schedules, (schedule: Schedule) => schedule?.weekday).map(
        (schedule) => schedule?.day
      )
    : [],

  arrayRangePickers: props.sequence?.schedules
    ? groupScheduleConfig(props.sequence?.schedules, (schedule: Schedule) => schedule?.weekday)
    : [],
});

const comparisonForm = cloneDeep(formState);

// Valida que si hay dias seleccionados, los rangos esten completos
const isValidRangePickers = computed(() => {
  if (!formState.daysSelected?.length) return true;
  const isValid = formState.arrayRangePickers?.every((picker) => {
    return picker.hours.every((hour) => hour.start && hour.end);
  });

  return isValid;
});

// onMounted(() => {
//   emit("toggleButton", false);
// });

watch(formState, () => {
  const isDisabled = isEqual(formState, comparisonForm);

  errorMessage.value = isValidRangePickers.value ? "" : "Complete los horarios";

  emit("toggleButton", isDisabled || !isValidRangePickers.value);
});

const nameDay = (index: number) => {
  const days = transformIndexDays([index]);
  return days[0]?.day;
};

const sortDataSource = (dataSource: RawSchedule[] | undefined) => {
  if (!dataSource) return;

  const data = [...dataSource];
  return data.sort((a, b) => a.order - b.order);
};

const addDayToList = (day: number) => {
  const itemList = {
    order: day,
    day: day,
    nameDay: nameDay(day),
    hours: [
      {
        id: `range-${day}-1`,
        start: "",
        end: "",
      },
    ],
  };

  if (formState.arrayRangePickers) formState.arrayRangePickers.push(itemList);
};

const removeDayToList = (day: number) => {
  formState.arrayRangePickers = formState.arrayRangePickers?.filter((item) => item.order !== day);
};

const setDaysSelected = (newDaysSelected: number[]) => {
  const noDaysSelected = formState.daysSelected?.filter(
    (daySelected) => !newDaysSelected.includes(daySelected)
  );
  noDaysSelected?.forEach((day) => {
    removeDayToList(day);
  });

  formState.daysSelected = newDaysSelected;
  formState.daysSelected.forEach((day) => {
    if (!formState.arrayRangePickers) return;

    const data = [...formState.arrayRangePickers].find((item) => item.order === day);
    if (data === undefined) {
      addDayToList(day);
    }
  });
};

const handleChangeRange = (
  order: number,
  idRange: number | string,
  newRange: { start: string; end: string }
) => {
  const data = formState.arrayRangePickers?.find((item) => {
    return item.order === order;
  });
  const range = data?.hours.find((hour) => {
    return hour.id == idRange;
  });

  if (!range) return;

  range.start = newRange.start;
  range.end = newRange.end;
};

const handleAddRange = (order: number) => {
  const day = formState.arrayRangePickers?.find((item) => item.order === order);
  const newRange = {
    id: `range-${day?.day}-${(day?.hours?.length || 0) + 1}`,
    start: "",
    end: "",
  };

  if (day !== undefined) {
    day.hours.push(newRange);
  }
};

const handleRemoveRange = (order: number, keyRange: number | string) => {
  const dayObj = formState.arrayRangePickers?.find((item) => item.order === order);

  if (!dayObj) return;

  dayObj.hours = dayObj.hours.filter((hour) => hour.id !== keyRange);
};

const arrayRangePickers = ref();
const errorMessage = ref("");

const getFormState = (): ScheduleStepDto => {
  if (errorMessage.value) throw new Error("Incomplete");

  return {
    schedules: formatSchedulePicker(formState.arrayRangePickers) || [],
  };
};

defineExpose({ getFormState, formRef });
</script>

<style scoped lang="sass">
h3
  font-size: 20px
  color: $gray-9
  margin-bottom: 16px

h4
  color: $gray-9
  font-size: 14px
  margin-bottom: 16px
  font-weight: 600

header
  width: 100%
  text-align: center
  margin-bottom: 25px

  p
    color: $text-secondary
    margin: 0
    font-size: 16px

.form
  height: 100%

.day-picker
  margin-bottom: 0
  :deep(label)
    font-weight: 600

.align-left
  text-align: left

.modal-description
  color: $gray-13

.margin-bottom-0
  margin-bottom: 0

.audio-description
  margin-top: 1em

.description-subtitle
  margin-bottom: 0
  font-weight: 600
  color: #262626E0

.error
  color: red
</style>
